<template>
  <div class="login-page" :class="[isStaging ? 'env-staging' : 'env-production', 'theme-' + resolvedTheme]">

    <!-- Light / dark toggle. Defaults to the system setting; a click pins a
         choice for this browser. Sits over the form side so it reads on both. -->
    <button
      type="button"
      class="theme-toggle"
      :aria-label="resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
      :title="resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleTheme"
    >
      <svg v-if="resolvedTheme === 'dark'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><circle cx="12" cy="12" r="4.2"/><path stroke-linecap="round" d="M12 2.5v2.2M12 19.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6"/></svg>
      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M21 12.8A8.5 8.5 0 1111.2 3a6.6 6.6 0 009.8 9.8z"/></svg>
    </button>

    <!-- LEFT: brand / info panel (hidden on small screens) -->
    <aside class="brand-panel">
      <div class="brand-wordmark">
        <img :src="logo" alt="Custimoo" class="brand-logo" />
        <span class="brand-subtitle">Admin Console</span>
      </div>

      <div class="brand-copy">
        <h1>Run your custom-apparel business <span>from one place.</span></h1>
        <p>
          The Custimoo Admin console gives your team control over orders,
          products, companies and payouts — all in one dashboard.
        </p>
        <ul class="brand-features">
          <li>Manage orders, rosters and production across all connected shops</li>
          <li>Configure companies, templates and storefront features</li>
          <li>Track usage, budgets and payouts in real time</li>
        </ul>
      </div>

      <div class="brand-foot">
        <span>&copy; {{ currentYear }} Custimoo</span>
      </div>
    </aside>

    <!-- RIGHT: form panel -->
    <main class="form-panel">
      <div class="form-panel-inner">

        <!-- Compact brand header shown only on small screens -->
        <div class="mobile-brand">
          <img :src="logo" alt="Custimoo" class="brand-logo" />
          <span class="brand-subtitle">Admin Console</span>
        </div>

        <div class="login-card">
          <span class="env-badge">
            <span class="env-dot"></span> {{ isStaging ? 'Staging' : 'Production' }}
          </span>

          <h2>Sign in to Custimoo Admin</h2>
          <p class="login-sub">
            Use your admin account credentials. Contact your workspace owner if you need access.
          </p>

          <div v-if="ssoError" class="sso-error" role="alert">{{ ssoError }}</div>

          <CForm @submit.prevent="Login">
            <CInput
              placeholder="Email"
              autocomplete="username email"
              name="email"
              v-model="form.email"
              v-validate="{required: true, min: 3, email: true}"
            >
              <template #prepend-content><CIcon name="cil-user"/></template>
            </CInput>
            <div v-if="errors" class="error_msg">
              {{errors.first('email')}}
            </div>

            <!-- Custimoo staff sign in with Microsoft, so a @custimoo.com email asks
                 for no password: Continue goes straight to Microsoft. The password
                 field comes back for everyone else, and for staff who choose "Use a
                 password instead" (the break-glass accounts). -->
            <template v-if="!ssoFirst">
              <CInput
                placeholder="Password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                name="password"
                v-model="form.password"
                v-validate="{required: true}"
              >
                <template #prepend-content><CIcon name="cil-lock-locked"/></template>
                <template #append-content>
                  <button
                    type="button"
                    class="pw-toggle"
                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    :title="showPassword ? 'Hide password' : 'Show password'"
                    @click="showPassword = !showPassword"
                  >
                    <svg v-if="!showPassword" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.04 12.32a1.01 1.01 0 010-.64C3.42 7.51 7.36 4.5 12 4.5s8.58 3.01 9.96 7.18c.07.21.07.43 0 .64C20.58 16.49 16.64 19.5 12 19.5S3.42 16.49 2.04 12.32z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    <svg v-else width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.22A10.48 10.48 0 002.04 11.68a1.01 1.01 0 000 .64C3.42 16.49 7.36 19.5 12 19.5c1.53 0 3-.33 4.33-.92M6.75 6.02A10.45 10.45 0 0112 4.5c4.64 0 8.58 3.01 9.96 7.18.07.21.07.43 0 .64a10.5 10.5 0 01-1.94 3.46M9.88 9.88a3 3 0 104.24 4.24M3 3l18 18"/></svg>
                  </button>
                </template>
              </CInput>
              <div v-if="errors" class="error_msg">
                {{errors.first('password')}}
              </div>

              <div class="login-links">
                <router-link to="/forgot-password">Forgot password?</router-link>
              </div>

            </template>
            <p v-else class="sso-note" data-test="sso-note">
              Custimoo staff sign in with their Microsoft account. No password needed here.
            </p>

            <CButton
              color="primary"
              type="submit"
              class="login-btn"
              :disabled="submitted"
            >
              <CSpinner v-if="submitted" size="sm" class="mr-2" />
              <template v-if="ssoFirst">
                <svg v-if="!submitted" class="sso-btn-ic mr-2" width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                {{ submitted ? 'Taking you to Microsoft…' : 'Continue with Microsoft' }}
              </template>
              <template v-else>{{ submitted ? 'Signing in…' : 'Sign in' }}</template>
            </CButton>
          </CForm>

          <!-- The way round Microsoft: only the break-glass accounts (kept in the
               server's environment) may sign in with a password, and only they are
               shown this link. The server says whether the typed email is one of
               them; for every other staff email there is no password option. -->
          <div v-if="isCustimooEmail && passwordAllowed" class="login-switch">
            <button
              v-if="!usePassword"
              type="button"
              class="login-switch-btn"
              data-test="use-password"
              @click="switchToPassword"
            >
              Use a password instead
            </button>
            <button
              v-else
              type="button"
              class="login-switch-btn"
              data-test="use-microsoft"
              @click="usePassword = false"
            >
              Sign in with Microsoft instead
            </button>
          </div>

          <div class="login-card-foot">
            Protected area — for authorized Custimoo staff and merchants only.<br>
            Trouble signing in? <a href="mailto:DDI@custimoo.com">Contact support</a>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import {handleResponseException, handleUserLoginLogOutAction} from "@/helpers/Helpers";
import {noTokenRequest} from "@/config/httpCommon"
import Store from "../../store";
import { clearImpersonation } from "@/helpers/impersonation";

export default {
  name: 'Login',
  data(){
    return{
      form:{
        email: '',
        password: '',
      },
      submitted: false,
      showPassword: false,
      // A staff email goes to Microsoft unless the person asked for the password
      // field (break-glass). Never remembered: every visit starts on Microsoft.
      usePassword: false,
      // Whether the server offers a password for the staff email in the field
      // (break-glass accounts only). False until it says so, and on any error.
      passwordAllowed: false,
      optionsTimer: null,
      ssoError: '',
      // Same asset the sidebar uses — the current official logo.
      logo: '/img/custimoo-logo.svg',
      // Light/dark for the login page. `themePref` is what the person pinned
      // ('light'|'dark') or null to follow the system; `systemDark` tracks the
      // OS setting live. The legacy admin has no global token system, so this is
      // self-contained and scoped to this page via a theme-* class on the root.
      themePref: null,
      systemDark: false,
      mediaQuery: null,
      onSystemChange: null,
    }
  },
  computed: {
    // The theme actually applied: a pinned choice wins, otherwise the OS.
    resolvedTheme(){
      return this.themePref || (this.systemDark ? 'dark' : 'light');
    },
    isStaging(){
      // Staging build points at devapi; anything not built for production
      // (local dev included) is treated as non-production.
      const baseUrl = process.env.VUE_APP_BASE_URL || '';
      return baseUrl.includes('devapi') || process.env.NODE_ENV !== 'production';
    },
    currentYear(){
      return new Date().getFullYear();
    },
    // Identity-first: a @custimoo.com email signs in with Microsoft. Purely a
    // UX decision here; the backend is the real gate (it checks the domain and
    // tenant, only lets in staff it already knows, and refuses a staff password
    // unless the account is break-glass).
    isCustimooEmail(){
      return /@custimoo\.com\s*$/i.test((this.form.email || '').trim());
    },
    // True while this sign-in goes to Microsoft and asks for no password.
    ssoFirst(){
      return this.isCustimooEmail && !this.usePassword;
    },
  },
  watch: {
    // Ask the server what to offer for a staff email, a moment after typing
    // stops. A changed email always starts again on Microsoft with no link.
    'form.email'(now, before){
      // Only a different address counts: trimming a pasted space on submit
      // must not throw a break-glass sign-in back to Microsoft.
      if ((now || '').trim().toLowerCase() === (before || '').trim().toLowerCase()) return;
      this.passwordAllowed = false;
      this.usePassword = false;
      clearTimeout(this.optionsTimer);
      if (!this.isCustimooEmail) return;
      const email = (this.form.email || '').trim();
      this.optionsTimer = setTimeout(() => this.loadSignInOptions(email), 350);
    },
  },
  created(){
    // The backend sends staff back here with ?sso_error=<code> when a Microsoft
    // sign-in is refused (not a Custimoo email, not a provisioned user, etc.).
    const code = this.$route && this.$route.query ? this.$route.query.sso_error : null;
    if (code) {
      this.ssoError = this.ssoErrorMessage(String(code));
    }
    this.initTheme();
  },
  beforeDestroy(){
    clearTimeout(this.optionsTimer);
    // Stop tracking the OS setting when we leave the login page.
    if (this.mediaQuery && this.onSystemChange) {
      try {
        if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener('change', this.onSystemChange);
        else if (this.mediaQuery.removeListener) this.mediaQuery.removeListener(this.onSystemChange);
      } catch (e) { /* no-op */ }
    }
  },
  methods: {
    // Read any pinned preference, seed the system setting, and follow the OS
    // live while the person hasn't pinned a choice. All guarded — a private
    // window or an old browser must never break the sign-in page.
    initTheme(){
      try {
        const saved = localStorage.getItem('admin_login_theme_v1');
        if (saved === 'light' || saved === 'dark') this.themePref = saved;
      } catch (e) { /* localStorage unavailable */ }

      try {
        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.systemDark = this.mediaQuery.matches;
        this.onSystemChange = (e) => { this.systemDark = e.matches; };
        if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener('change', this.onSystemChange);
        else if (this.mediaQuery.addListener) this.mediaQuery.addListener(this.onSystemChange);
      } catch (e) { /* matchMedia unavailable — falls back to light */ }
    },
    // Pin the opposite of what's showing, and remember it for next time.
    toggleTheme(){
      this.themePref = this.resolvedTheme === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('admin_login_theme_v1', this.themePref); } catch (e) { /* no-op */ }
    },
    // Full-page redirect to the backend, which starts the Microsoft OAuth flow.
    // The email goes along as a hint, so Microsoft opens on that account
    // without asking which one. The backend only passes on a staff email.
    ssoLogin(){
      window.location.href = this.ssoUrl();
    },
    ssoUrl(){
      const base = (process.env.VUE_APP_BASE_URL || '').replace(/\/+$/, '');
      const email = (this.form.email || '').trim();
      return base + '/auth/microsoft/redirect' + (email ? '?login_hint=' + encodeURIComponent(email) : '');
    },
    loadSignInOptions(email){
      return noTokenRequest.get('auth/sign-in-options', { params: { email } }).then(response => {
        // The answer is for the email it was asked about; ignore it if the
        // field has moved on since.
        if ((this.form.email || '').trim() !== email) return;
        const result = response && response.data ? response.data.result : null;
        this.passwordAllowed = !!(result && result.staff && result.password);
      }).catch(() => {
        this.passwordAllowed = false;
      });
    },
    switchToPassword(){
      this.usePassword = true;
      this.ssoError = '';
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('input[name="password"]');
        if (el) el.focus();
      });
    },
    ssoErrorMessage(code){
      const map = {
        not_custimoo: 'That is not a Custimoo email. Use your @custimoo.com account, or sign in with a password.',
        not_provisioned: 'No admin account was found for that Custimoo email. Ask an admin to create your account first.',
        wrong_tenant: 'That account is not in the Custimoo directory.',
        sso_failed: 'Microsoft sign-in could not be completed. Please try again.',
        sso_misconfigured: 'Microsoft sign-in is not fully set up yet. Please tell your administrator.',
      };
      return map[code] || 'Microsoft sign-in was not completed. Please try again.';
    },
    async Login(){
      // A pasted email often carries a space; the email rule would reject it.
      const trimmed = (this.form.email || '').trim();
      if (trimmed !== this.form.email) {
        this.form.email = trimmed;
        await this.$nextTick();
      }
      // A staff email: check the email only, then hand over to Microsoft.
      if (this.ssoFirst) {
        if (await this.$validator.validate('email')) {
          this.submitted = true;
          this.ssoLogin();
        }
        return;
      }
      let valid = await this.$validator.validate();
      if(valid){
        this.submitted = true;
        let login_data = {
          email: this.form.email,
          password: this.form.password,
        }
       await noTokenRequest.post("login", login_data).then(async successResponse => {
         let response_data = successResponse.data;
         if(response_data.success) {
           // A fresh form login is never an impersonated session: drop any
           // leftover stash from a crashed/expired impersonation so the
           // banner (and its Exit restore) can never leak across users.
           clearImpersonation();
           await handleUserLoginLogOutAction('login', response_data.result);
         } else {
           this.showServerErrors(successResponse)
         }
         this.submitted = false;
        }).catch(error => {
         this.submitted = false;
          handleResponseException(error)
        });
      }
    }
  }
}
</script>
<style scoped>
  .login-page{
    /* Light theme (default) — the form side's tokens. The brand panel keeps its
       own dark identity in both themes; only the form side flips. */
    --lp-page-bg: #ebedef;
    --lp-card-bg: #ffffff;
    --lp-card-border: #d8dbe0;
    --lp-shadow: 0 10px 40px rgba(17, 24, 39, 0.08);
    --lp-ink: #23282f;
    --lp-ink-soft: #5b6675;
    --lp-ink-mute: #768192;
    --lp-divider: #d8dbe0;
    --lp-input-bg: #ffffff;
    --lp-input-border: #d8dbe0;
    --lp-input-ink: #23282f;
    --lp-accent: #321fdb;
    --lp-btn-ink: #23282f;
    --lp-badge-bg: #e6f3f1;
    --lp-badge-ink: #0c6b5a;
    --lp-badge-border: #b5e3da;
    --lp-toggle-bg: rgba(255, 255, 255, 0.9);
    --lp-toggle-border: #d8dbe0;
    --lp-toggle-ink: #3c4b64;

    display: flex;
    min-height: 100vh;
    background: var(--lp-page-bg);
  }
  /* Dark theme — the form side only. */
  .login-page.theme-dark{
    --lp-page-bg: #0e1013;
    --lp-card-bg: #16181c;
    --lp-card-border: #262b31;
    --lp-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
    --lp-ink: #f3f5f7;
    --lp-ink-soft: #aeb6bf;
    --lp-ink-mute: #8a94a0;
    --lp-divider: #262b31;
    --lp-input-bg: #1b1e23;
    --lp-input-border: #2b3138;
    --lp-input-ink: #f3f5f7;
    --lp-accent: #6b90f3;
    --lp-btn-ink: #f3f5f7;
    --lp-badge-bg: #12301f;
    --lp-badge-ink: #6fd6b4;
    --lp-badge-border: #1c5a43;
    --lp-toggle-bg: rgba(30, 34, 40, 0.9);
    --lp-toggle-border: #2b3138;
    --lp-toggle-ink: #cfd6de;
  }

  /* Light/dark toggle — floats top-right, over the form side. */
  .theme-toggle{
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 5;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: var(--lp-toggle-bg);
    border: 1px solid var(--lp-toggle-border);
    color: var(--lp-toggle-ink);
    cursor: pointer;
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
    transition: border-color .12s ease, color .12s ease, transform .12s ease;
  }
  .theme-toggle:hover{ color: var(--lp-accent); border-color: var(--lp-accent); transform: translateY(-1px); }
  .theme-toggle:focus-visible{ outline: 2px solid var(--lp-accent); outline-offset: 2px; }

  /* ---------- Brand panel (left) ---------- */
  .brand-panel{
    flex: 1 1 46%;
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 3rem 3.5rem;
    position: relative;
    overflow: hidden;
  }
  /* Production: the admin portal's own identity — charcoal (top header /
     sidebar tones) with the teal accent from the custimoo logo (#14a892). */
  .env-production .brand-panel{
    background:
      radial-gradient(1100px 600px at -10% -10%, rgba(20,168,146,.30), transparent 60%),
      radial-gradient(900px 500px at 110% 110%, rgba(20,168,146,.12), transparent 55%),
      linear-gradient(160deg, #23282f, #2f353a 55%, #3c4b64);
  }
  /* Staging: amber-tinted so environments are distinguishable at a glance. */
  .env-staging .brand-panel{
    background:
      radial-gradient(1100px 600px at -10% -10%, rgba(245,158,11,.32), transparent 60%),
      radial-gradient(900px 500px at 110% 110%, rgba(251,191,36,.14), transparent 55%),
      linear-gradient(160deg, #1c1917, #292018 55%, #3b2f1e);
  }
  .brand-panel::after{
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px);
    background-size: 44px 44px;
    -webkit-mask-image: radial-gradient(700px 500px at 30% 30%, #000 30%, transparent 75%);
    mask-image: radial-gradient(700px 500px at 30% 30%, #000 30%, transparent 75%);
    pointer-events: none;
  }
  .brand-wordmark{
    display: flex;
    align-items: baseline;
    gap: .75rem;
    position: relative;
  }
  .brand-logo{
    height: 30px;
    width: auto;
  }
  .brand-subtitle{
    font-size: .7rem;
    font-weight: 600;
    letter-spacing: .14em;
    text-transform: uppercase;
  }
  .env-production .brand-subtitle{ color: #2ec4a5; }
  .env-staging .brand-subtitle{ color: #fcd34d; }

  .brand-copy{
    position: relative;
    max-width: 440px;
  }
  .brand-copy h1{
    font-size: clamp(1.6rem, 2.6vw, 2.2rem);
    line-height: 1.2;
    font-weight: 800;
    letter-spacing: -.02em;
    margin-bottom: .9rem;
  }
  .env-production .brand-copy h1 span{ color: #2ec4a5; }
  .env-staging .brand-copy h1 span{ color: #fcd34d; }
  .brand-copy p{
    color: rgba(255,255,255,.78);
    line-height: 1.65;
    font-size: .97rem;
  }
  .brand-features{
    margin: 1.6rem 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: .8rem;
  }
  .brand-features li{
    position: relative;
    padding-left: 1.6rem;
    color: rgba(255,255,255,.85);
    font-size: .92rem;
    line-height: 1.45;
  }
  .brand-features li::before{
    content: "✓";
    position: absolute;
    left: 0;
    top: 0;
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 6px;
    font-size: .7rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .env-production .brand-features li::before{
    background: rgba(20,168,146,.22);
    border: 1px solid rgba(46,196,165,.4);
    color: #7fe0cd;
  }
  .env-staging .brand-features li::before{
    background: rgba(245,158,11,.22);
    border: 1px solid rgba(252,211,77,.4);
    color: #fde68a;
  }

  .brand-foot{
    position: relative;
    color: rgba(255,255,255,.5);
    font-size: .8rem;
    display: flex;
    gap: 1.1rem;
    flex-wrap: wrap;
  }

  /* ---------- Form panel (right) ---------- */
  .form-panel{
    flex: 1 1 54%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1.5rem;
  }
  .form-panel-inner{
    width: 100%;
    max-width: 430px;
  }
  .mobile-brand{ display: none; }

  .login-card{
    background: var(--lp-card-bg);
    border: 1px solid var(--lp-card-border);
    border-radius: 14px;
    box-shadow: var(--lp-shadow);
    padding: 2.4rem 2.2rem 2rem;
    transition: background-color .18s ease, border-color .18s ease;
  }
  .env-badge{
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    border-radius: 999px;
    padding: .25rem .75rem;
    margin-bottom: 1.1rem;
  }
  .env-dot{
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
  .env-production .env-badge{
    color: var(--lp-badge-ink);
    background: var(--lp-badge-bg);
    border: 1px solid var(--lp-badge-border);
  }
  .env-production .env-dot{ background: #14a892; }
  .env-staging .env-badge{
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }
  .theme-dark.env-staging .env-badge{
    color: #fcd34d;
    background: #2c2410;
    border-color: #5a4a1c;
  }
  .env-staging .env-dot{ background: #d97706; }

  .login-card h2{
    font-size: 1.45rem;
    font-weight: 700;
    letter-spacing: -.02em;
    margin-bottom: .35rem;
    color: var(--lp-ink);
  }
  .login-sub{
    color: var(--lp-ink-mute);
    font-size: .92rem;
    margin-bottom: 1.6rem;
    line-height: 1.5;
  }

  .form-group{
    margin: 1rem 0 0 0;
  }
  .pw-toggle{
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--lp-ink-mute);
    border: 0;
    background: transparent;
    padding: 0;
  }
  .pw-toggle:focus-visible{
    outline: 2px solid var(--lp-accent);
    outline-offset: 2px;
  }
  .pw-toggle:hover{ color: var(--lp-accent); }

  .login-links{
    display: flex;
    justify-content: flex-end;
    margin: .6rem 0 1.2rem;
    font-size: .87rem;
  }
  .login-links a{ font-weight: 600; color: var(--lp-accent); }

  .login-btn{
    width: 100%;
    padding: .65rem 1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* M365 SSO */
  .sso-error{
    background: #fdeded;
    border: 1px solid #f5c2c7;
    color: #842029;
    border-radius: 6px;
    padding: .6rem .8rem;
    font-size: .82rem;
    margin-bottom: 1rem;
  }
  .sso-note{
    margin: .2rem 0 1.2rem;
    color: var(--lp-ink-soft);
    font-size: .87rem;
    line-height: 1.45;
  }
  .login-switch{
    margin-top: .9rem;
    text-align: center;
  }
  .login-switch-btn{
    background: none;
    border: 0;
    padding: .2rem .4rem;
    color: var(--lp-ink-mute);
    font-size: .82rem;
    text-decoration: underline;
    cursor: pointer;
  }
  .login-switch-btn:hover{ color: var(--lp-accent); }
  .login-switch-btn:focus-visible{
    outline: 2px solid var(--lp-accent);
    outline-offset: 2px;
    border-radius: 3px;
  }
  .sso-btn-ic{ flex: none; }

  .login-card-foot{
    margin-top: 1.6rem;
    padding-top: 1.2rem;
    border-top: 1px solid var(--lp-divider);
    text-align: center;
    color: var(--lp-ink-mute);
    font-size: .78rem;
    line-height: 1.7;
  }
  .login-card-foot a{ font-weight: 600; color: var(--lp-accent); }

  /* CoreUI inputs render their own markup; theme them through the tokens so the
     fields, the prepend icons and the placeholders all follow light/dark. */
  .login-card ::v-deep .form-control,
  .login-card ::v-deep .input-group-text{
    background-color: var(--lp-input-bg);
    border-color: var(--lp-input-border);
    color: var(--lp-input-ink);
    transition: background-color .18s ease, border-color .18s ease, color .18s ease;
  }
  .login-card ::v-deep .input-group-text{
    color: var(--lp-ink-mute);
  }
  .login-card ::v-deep .form-control::placeholder{
    color: var(--lp-ink-mute);
    opacity: 1;
  }
  .login-card ::v-deep .form-control:focus{
    background-color: var(--lp-input-bg);
    border-color: var(--lp-accent);
    color: var(--lp-input-ink);
    box-shadow: 0 0 0 2px rgba(107, 144, 243, 0.18);
  }
  /* Browser autofill paints its own (light) background straight onto the input
     and ignores `background-color`, so an autofilled email/password used to
     show up as a white field in dark mode. Chrome does honour an inset
     box-shadow and `-webkit-text-fill-color`, so paint the field with our own
     token colours instead; the 9999s background-color transition keeps the
     browser's delayed yellow repaint from ever surfacing. Driven by the same
     tokens, it holds in both light and dark. */
  .login-card ::v-deep .form-control:-webkit-autofill,
  .login-card ::v-deep .form-control:-webkit-autofill:hover,
  .login-card ::v-deep .form-control:-webkit-autofill:focus,
  .login-card ::v-deep .form-control:-webkit-autofill:active{
    -webkit-text-fill-color: var(--lp-input-ink);
    caret-color: var(--lp-input-ink);
    -webkit-box-shadow: 0 0 0 1000px var(--lp-input-bg) inset;
    box-shadow: 0 0 0 1000px var(--lp-input-bg) inset;
    border-color: var(--lp-input-border);
    transition: background-color 9999s ease-in-out 0s, border-color .18s ease;
  }
  .login-card ::v-deep .form-control:-webkit-autofill:focus{
    border-color: var(--lp-accent);
  }
  /* Dark: soften the CoreUI validation-red error banner to fit the surface. */
  .theme-dark .sso-error{
    background: #2c1512;
    border-color: #5a2620;
    color: #f3b0a8;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 900px){
    .login-page{ flex-direction: column; }
    .brand-panel{ display: none; }
    .mobile-brand{
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: .4rem;
      padding: 2.2rem 1.5rem 3.2rem;
      margin: -3rem -1.5rem 0;
      text-align: center;
    }
    .env-production .mobile-brand{
      background: linear-gradient(160deg, #23282f, #3c4b64);
    }
    .env-staging .mobile-brand{
      background: linear-gradient(160deg, #1c1917, #3b2f1e);
    }
    .form-panel{ align-items: flex-start; padding-top: 3rem; }
    .login-card{ margin-top: -1.6rem; position: relative; }
  }
  @media (max-width: 400px){
    .login-card{ padding: 1.8rem 1.2rem 1.4rem; }
  }
</style>
